// Microsoft Graph from the browser (O-R56, owner 2026-10-22; ported from Summit Engineering's auth\graph.ts). Two
// delegated scopes, each acquired separately from the API token and silently first:
//   • User.Read — the signed-in person's own profile and photo (normally pre-consented). /me/manager needs
//     User.Read.All, which is not asked for, so the person's own sign-in does not read the manager.
//   • User.ReadBasic.All — an Admin's refresh of names and photos of the people Summit Operations knows.
// No secret and no application permission here. A Graph failure is never an error the person has to deal with.

export const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

export const GRAPH_SCOPES = {
  userRead: "https://graph.microsoft.com/User.Read",
  userReadBasicAll: "https://graph.microsoft.com/User.ReadBasic.All"
} as const;

export type GraphScope = keyof typeof GRAPH_SCOPES;

/** silent = never shows anything; interactive = a pop-up (redirect if the pop-up is blocked), from a click only. */
export type GraphTokenMode = "silent" | "interactive";

export interface GraphTokenResult {
  token: string | null;
  /** True when Microsoft says an administrator must approve the permission (tenant consent policy). */
  consentBlocked?: boolean;
}

export type GraphTokenProvider = (scope: GraphScope, mode: GraphTokenMode) => Promise<GraphTokenResult>;

let provider: GraphTokenProvider | null = null;
const listeners = new Set<() => void>();

/** Registered by the Microsoft (Entra) sign-in only; the development sign-in never registers one, so Graph is skipped. */
export function setGraphTokenProvider(next: GraphTokenProvider | null): void {
  provider = next;
  listeners.forEach((l) => l());
}

export function getGraphTokenProvider(): GraphTokenProvider | null {
  return provider;
}

export function subscribeGraphTokenProvider(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Microsoft's "an administrator must approve this" answers (AADSTS90094/90095, admin consent required). */
export function isAdminConsentError(error: unknown): boolean {
  const text = `${(error as { errorCode?: string })?.errorCode ?? ""} ${(error as { errorMessage?: string })?.errorMessage ?? ""} ${(error as Error)?.message ?? ""}`;
  return /AADSTS(90094|90095|65004)|admin(istrator)?[ _-]?(approval|consent)|consent_required_admin/i.test(text);
}

export const ME_SELECT = "id,displayName,givenName,surname,jobTitle,department,employeeId,mail,userPrincipalName";
export const BASIC_SELECT = "id,displayName,givenName,surname,mail,userPrincipalName";

export interface GraphMe {
  id?: string | null;
  displayName?: string | null;
  givenName?: string | null;
  surname?: string | null;
  jobTitle?: string | null;
  department?: string | null;
  employeeId?: string | null;
  mail?: string | null;
  userPrincipalName?: string | null;
}

export type GraphPhotoState = "present" | "none" | "unknown";

type Fetch = typeof fetch;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** One GET with a single polite retry on 429 (Graph's Retry-After, at most 10 s here). */
export async function graphGet(fetcher: Fetch, token: string, path: string, accept = "application/json"): Promise<Response> {
  const headers = { Authorization: `Bearer ${token}`, Accept: accept };
  let response = await fetcher(`${GRAPH_BASE}${path}`, { headers });
  if (response.status === 429) {
    const seconds = Number(response.headers.get("Retry-After") ?? "2");
    await wait(Math.min(Number.isFinite(seconds) ? seconds : 2, 10) * 1000);
    response = await fetcher(`${GRAPH_BASE}${path}`, { headers });
  }
  return response;
}

/** The 240×240 photo, then the default size; a 404 means Microsoft has no photo for the person. */
export async function readPhoto(fetcher: Fetch, token: string, base: string): Promise<{ photo: Blob | null; state: GraphPhotoState }> {
  let sawNotFound = false;
  for (const path of [`${base}/photos/240x240/$value`, `${base}/photo/$value`]) {
    try {
      const response = await graphGet(fetcher, token, path, "image/*");
      if (response.ok) return { photo: await response.blob(), state: "present" };
      if (response.status === 404) sawNotFound = true;
      else return { photo: null, state: "unknown" };
    } catch {
      return { photo: null, state: "unknown" };
    }
  }
  return { photo: null, state: sawNotFound ? "none" : "unknown" };
}

/** The signed-in person's own profile (User.Read). Throws only when /me itself cannot be read. */
export async function readMe(token: string, fetcher: Fetch = fetch): Promise<{ me: GraphMe; photo: Blob | null; photoState: GraphPhotoState }> {
  const response = await graphGet(fetcher, token, `/me?$select=${ME_SELECT}`);
  if (!response.ok) throw new Error(`Microsoft Graph /me answered ${response.status}.`);
  const me = (await response.json()) as GraphMe;
  const { photo, state } = await readPhoto(fetcher, token, "/me");
  return { me, photo, photoState: state };
}

export interface BasicRead {
  upn: string;
  found: boolean;
  user: GraphMe | null;
  photo: Blob | null;
  photoState: GraphPhotoState;
}

export class GraphConsentError extends Error {}

/** Names and photos of the listed people (User.ReadBasic.All), four at a time. 403 means the permission is missing. */
export async function readPeopleBasic(token: string, upns: readonly string[], fetcher: Fetch = fetch, concurrency = 4): Promise<BasicRead[]> {
  const results: BasicRead[] = [];
  let next = 0;
  let denied = false;
  async function worker(): Promise<void> {
    while (!denied && next < upns.length) {
      const upn = upns[next++]!;
      const base = `/users/${encodeURIComponent(upn)}`;
      const response = await graphGet(fetcher, token, `${base}?$select=${BASIC_SELECT}`);
      if (response.status === 403 || response.status === 401) {
        denied = true;
        return;
      }
      if (response.status === 404) {
        results.push({ upn, found: false, user: null, photo: null, photoState: "unknown" });
        continue;
      }
      if (!response.ok) continue;
      const user = (await response.json()) as GraphMe;
      const { photo, state } = await readPhoto(fetcher, token, base);
      results.push({ upn, found: true, user, photo, photoState: state });
    }
  }
  await Promise.all(Array.from({ length: Math.max(1, Math.min(concurrency, upns.length)) }, () => worker()));
  if (denied) throw new GraphConsentError("Microsoft did not allow reading other people's basic profiles.");
  return results;
}

/** A 256×256 JPEG under 200 KB (the server's photo rule), centre-cropped. Browser only. */
export async function toSquareJpeg(blob: Blob, size = 256): Promise<Blob> {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("The photo could not be prepared.");
    const side = Math.min(bitmap.width, bitmap.height);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, size, size);
    for (const quality of [0.85, 0.7, 0.55]) {
      const out = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
      if (out && out.size <= 200 * 1024) return out;
    }
    throw new Error("The photo is too large.");
  } finally {
    bitmap.close();
  }
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

/** The photo prepared for the API, or what to say about it. Never throws. */
export async function preparePhoto(photo: Blob | null, state: GraphPhotoState): Promise<{ photoState: GraphPhotoState; photoBase64: string | null; photoContentType: string | null }> {
  if (!photo) return { photoState: state, photoBase64: null, photoContentType: null };
  try {
    const jpeg = await toSquareJpeg(photo);
    return { photoState: "present", photoBase64: await blobToBase64(jpeg), photoContentType: "image/jpeg" };
  } catch {
    return { photoState: "unknown", photoBase64: null, photoContentType: null };
  }
}
